import { Row } from "@tanstack/react-table";
import {
  Booking,
  FeedbackLockReason,
  IcbtCommunication,
  IcbtRequest,
  ProgressByProgramId,
  SequentialUnlockItem,
} from "api/types";
import { logger } from "core/logger";
import { UserAnswer } from "core/types/index";
import {
  format,
  fromUnixTime,
  isBefore,
  isPast,
  isToday,
  parseISO,
} from "date-fns";
import { ProgramsAndTreatments } from "hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { QuestionnaireCounts } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { TFunction } from "react-i18next";
import * as R from "remeda";
import {
  AssignedTableRow,
  HighlightStatus,
  ProgramStatus,
  SelfExploredTableRow,
} from "./types";

const getSequentialUnlockItem = (
  icbtSequentialUnlock: SequentialUnlockItem[] | undefined,
  programId: string,
  treatmentId: string,
  icbtRequestId?: string | null,
): SequentialUnlockItem | undefined => {
  if (icbtRequestId) {
    return icbtSequentialUnlock?.find((item) => item.id === icbtRequestId);
  }
  return icbtSequentialUnlock?.find(
    (item) => item.programId === programId && item.treatmentId === treatmentId,
  );
};

export const getIsSequentiallyLocked = (
  icbtSequentialUnlock: SequentialUnlockItem[] | undefined,
  programId: string,
  treatmentId: string,
  icbtRequestId?: string | null,
): boolean =>
  getSequentialUnlockItem(
    icbtSequentialUnlock,
    programId,
    treatmentId,
    icbtRequestId,
  )?.isLocked ?? false;

export const getAssessment = (
  icbtSequentialUnlock: SequentialUnlockItem[] | undefined,
  programId: string,
  treatmentId: string,
  icbtRequestId?: string | null,
): SequentialUnlockItem["assessment"] | undefined =>
  getSequentialUnlockItem(
    icbtSequentialUnlock,
    programId,
    treatmentId,
    icbtRequestId,
  )?.assessment;

export const getIsFeedbackLocked = (
  icbtSequentialUnlock: SequentialUnlockItem[] | undefined,
  programId: string,
  treatmentId: string,
  icbtRequestId?: string | null,
): boolean =>
  getSequentialUnlockItem(
    icbtSequentialUnlock,
    programId,
    treatmentId,
    icbtRequestId,
  )?.feedbackLocked?.locked ?? false;

export const getFeedbackLockReason = (
  icbtSequentialUnlock: SequentialUnlockItem[] | undefined,
  programId: string,
  treatmentId: string,
  icbtRequestId?: string | null,
): FeedbackLockReason | undefined =>
  getSequentialUnlockItem(
    icbtSequentialUnlock,
    programId,
    treatmentId,
    icbtRequestId,
  )?.feedbackLocked?.reason;

export const getRequestingPsychologistId = (
  icbtSequentialUnlock: SequentialUnlockItem[] | undefined,
  programId: string,
  treatmentId: string,
  icbtRequestId?: string | null,
): string | undefined =>
  getSequentialUnlockItem(
    icbtSequentialUnlock,
    programId,
    treatmentId,
    icbtRequestId,
  )?.requestingPsychologistId;

// True when the logged-in psychologist is the one who requested this icbt,
// i.e. the treating psychologist who may generate/fetch AI feedback drafts.
//
// Requires a concrete icbtRequestId and matches the unlock item by id only.
// We deliberately avoid the programId/treatmentId fallback used elsewhere:
// when a program/treatment has been re-assigned there can be multiple
// sequential-unlock items for the same pair, and matching by pair could
// resolve to the wrong requesting psychologist. We fail closed (return false)
// when the id is missing rather than risk granting/denying drafts incorrectly.
export const getIsRequestOwner = (
  icbtSequentialUnlock: SequentialUnlockItem[] | undefined,
  icbtRequestId: string | null | undefined,
  loggedInPsychologistId: number | null | undefined,
): boolean => {
  if (!icbtRequestId || loggedInPsychologistId == null) return false;
  return (
    icbtSequentialUnlock?.find((item) => item.id === icbtRequestId)
      ?.requestingPsychologistId === String(loggedInPsychologistId)
  );
};

export const getFeedbackLockTranslationKey = (
  reason: FeedbackLockReason | undefined,
): "ICBT_FEEDBACK_LOCKED" | "ICBT_FEEDBACK_LOCKED_INELIGIBLE" => {
  if (reason === "INELIGIBLE") return "ICBT_FEEDBACK_LOCKED_INELIGIBLE";
  return "ICBT_FEEDBACK_LOCKED";
};

type ParseIcbtDataArgs = {
  allProgress: ProgressByProgramId | undefined;
  icbtRequests: IcbtRequest[] | undefined;
  programsAndTreatments: ProgramsAndTreatments | undefined;
  bookings: Booking[] | undefined;
  userAnswers: UserAnswer[] | undefined;
  userId: number | undefined;
  allCommunication: IcbtCommunication[] | undefined;
  t: TFunction<"translation", undefined>;
  questionnaireCounts: QuestionnaireCounts | undefined;
  sortableIcbtsEnabled: boolean;
  feedbackLockEnabled: boolean;
  icbtSequentialUnlock?: SequentialUnlockItem[];
};

const FALLBACK: AssignedTableRow = {
  id: "fallback",
  title: {
    programTitle: null,
    treatmentTitle: null,
    programId: null,
    status: null,
  },
  feedbackDate: {
    feedbackDate: null,
    feedbackDateIgnored: false,
    hasFeedback: false,
  },
  patientNotes: null,
  assigned: {
    assignedAt: null,
    assignedBy: "--",
  },
  status: null,
  metadata: {
    programId: "--",
    treatmentId: "--",
    icbtRequestId: "--",
    feedbackDueDate: null,
    feedbackDueDateIgnored: false,
    newProgressAfterLastMeeting: false,
    hasProgress: false,
    requestId: "--",
    assignedAt: new Date(0),
    isLatestRequest: false,
    feedbackDate: null,
    icbtFeedbackDateIgnored: false,
    hasNoFeedback: true,
    canExtendFeedbackDate: false,
  },
  icbtFeedbackState: { feedbacks: [], communication: null },
};

export const parseIcbtData = ({
  allProgress,
  icbtRequests,
  programsAndTreatments,
  bookings,
  userAnswers,
  userId,
  allCommunication,
  t,
  questionnaireCounts,
  sortableIcbtsEnabled,
  feedbackLockEnabled,
  icbtSequentialUnlock,
}: ParseIcbtDataArgs): {
  assignedTableRows: AssignedTableRow[];
  assignedNewProgressCount: number;
  selfExploredTableRows: SelfExploredTableRow[];
  selfExploredNewProgressCount: number;
} => {
  if (!allProgress || !icbtRequests || !programsAndTreatments || !bookings) {
    return {
      assignedTableRows: [FALLBACK],
      assignedNewProgressCount: 0,
      selfExploredTableRows: [],
      selfExploredNewProgressCount: 0,
    };
  }

  const now = new Date();
  const lastMeeting = R.pipe(
    bookings,
    R.sortBy(({ endsAt }) => endsAt),
    // map and filter only previous bookings to dates
    R.flatMap(({ endsAt }) => {
      const date = fromUnixTime(endsAt);
      return isBefore(date, now) ? [date] : [];
    }),
    R.last(),
  );

  const foundAssignIds = new Set();

  const uniqueBySurveyIdUserAnswersPerRequestId = R.pipe(
    userAnswers ?? [],
    R.groupBy(({ requestId }) => requestId),
    R.mapValues(R.uniqueBy(({ surveyId }) => surveyId)),
  );

  return R.pipe(
    allProgress,
    R.values,
    R.flatMap((progress) => [
      progress,
      ...R.pipe(progress.archivedProgress ?? [], R.reverse()), // We have to reverse because in archive last element is the most recent archived sprogress
    ]),
    R.flatMap((progress) => {
      const program = programsAndTreatments.programs[progress.programId];
      if (!program) {
        logger.error("Program not found for progress, should never happen", {
          userId,
          programId: progress.programId,
          service: "indianapolis", // to make it easier to follow in datadog
        });
        return []; // filter out progresses without matching program
      }
      const matchingRequests = R.sortBy.strict(
        icbtRequests.filter((r) => r.programId === progress.programId),
        [(x) => x.requestOrder, "asc"],
      );

      let matchingRequest = matchingRequests.find(
        (request) => request.id === progress.assignId,
      );

      if (!matchingRequest) {
        matchingRequest = R.pipe(
          matchingRequests ?? [],
          R.findLast(
            (request) =>
              request.programId === progress.programId &&
              !foundAssignIds.has(request.id),
          ),
        );
      }

      if (matchingRequest) {
        foundAssignIds.add(matchingRequest.id);

        // If there's no maxProgressedPageId, it means the user has not actually started the program. This is a empty "container" after resetting the progress in indy.
        if (!progress.maxProgressedPageId) {
          return {
            type: "assignedNotStarted", // discriminator
            request: matchingRequest,
            program,
          } as const;
        }

        // otherwise this is an actual progress
        return {
          type: "assignedAndStarted", // discriminator
          progress,
          request: matchingRequest,
          program,
        } as const;
      }
      return {
        type: "selfExplored", // discriminator
        progress,
        program,
      } as const;
    }),
    // Combine data for requests and concat with progress
    R.concat(
      icbtRequests.flatMap((request) => {
        const program = programsAndTreatments.programs[request.programId];
        if (!program) return []; // filter out assignments without matching program
        if (allProgress[program.id]) return []; // filter out assigments with progress, since they're included
        return {
          type: "assignedNotStarted", // discriminator
          request,
          program,
        } as const;
      }),
    ),
    R.map((entry) => {
      const startedAt = (() => {
        if (entry.type === "assignedNotStarted") return null;
        return entry.progress.startedAt
          ? parseISO(entry.progress.startedAt)
          : null;
      })();

      const completedAt = (() => {
        if (entry.type === "assignedNotStarted") return null;
        return entry.progress.completedAt
          ? parseISO(entry.progress.completedAt)
          : null;
      })();

      const lastProgressAt = (() => {
        if (entry.type === "assignedNotStarted") return null;
        return entry.progress.lastProgressAt
          ? parseISO(entry.progress.lastProgressAt)
          : null;
      })();
      const feedbackDate = entry.request?.feedbackDate ?? null;
      const patientSeenFeedbackAt = entry.request?.patientSeenAt ?? null;

      const communication = allCommunication?.find(
        (communication) => communication.request_id === entry?.request?.id,
      );

      const sortedFeedbacks = [...(communication?.feedbacks ?? [])]
        .filter((feedback) => !!feedback.feedback_text)
        .sort(
          (a, b) =>
            new Date(b.feedback_updated_at).getTime() -
            new Date(a.feedback_updated_at).getTime(),
        );
      const newestFeedback =
        sortedFeedbacks.length > 0 ? sortedFeedbacks[0] : undefined;

      const hasNoFeedback = newestFeedback === undefined;

      const icbtCommunicationProps = communication
        ? {
            feedback_date: communication?.feedback_date,
            feedback_date_ignored_at: communication?.feedback_date_ignored_at,
            request_id: communication?.request_id,
            feedback_seen_at: newestFeedback?.feedback_seen_at,
          }
        : undefined;

      // We want to indicate progress after last meeting only if it's the first time
      // user is making progress towards completion. Later completions only
      // update the "lastProgressAt" column.
      const highlightStatus = ((): HighlightStatus => {
        if (
          feedbackDate &&
          hasNoFeedback &&
          !icbtCommunicationProps?.feedback_date_ignored_at &&
          isPast(new Date(feedbackDate)) &&
          !isToday(new Date(feedbackDate))
        )
          return "alert";
        if (!lastMeeting || !lastProgressAt) return "none";
        if (completedAt && isBefore(completedAt, lastMeeting)) return "none";
        if (isBefore(lastMeeting, lastProgressAt)) return "info";
        return null;
      })();

      const { program } = entry;
      // Use standalone programs title if program is standalone
      // Otherwise use treatment title + programs location in treatment
      const indexOfProgram =
        programsAndTreatments.treatments[
          program.treatment.id
        ].programs.findIndex((p) => p.id === program.id) + 1;

      const treatmentTitle = program.standalone
        ? t("ICBT_STANDALONE_PROGRAMS")
        : `${program.treatment.title} • ${t("PROGRAM")} ${indexOfProgram}`;

      const status =
        entry.type === "assignedNotStarted"
          ? null
          : ({
              startedAt: startedAt ?? null,
              completedAt: completedAt ?? null,
              percentage: !entry.progress.totalPages // if totalPages, default to 0%
                ? 0
                : ((entry.progress.maxProgressedPageIndex + 1) /
                    entry.progress.totalPages) *
                  100,
            } satisfies ProgramStatus);

      const sharedCellsAndData = {
        id: entry.request?.id ?? "",
        title: {
          programTitle: program.title,
          treatmentTitle,
          programId: program.id,
          status,
        },
        feedbackDate: {
          feedbackDate,
          feedbackDateIgnored: Boolean(
            icbtCommunicationProps?.feedback_date_ignored_at,
          ),
          hasFeedback: !hasNoFeedback,
        },
        patientNotes: {
          // default to 0 for all counts below
          patientNotesCount: communication?.responses?.length ?? 0,
          questionnairesInProgramAnswered:
            uniqueBySurveyIdUserAnswersPerRequestId[
              communication?.request_id ?? ""
            ]?.length ?? 0,
          totalQuestionnairesInProgram: questionnaireCounts?.[program.id] ?? 0,
        },
        icbtFeedbackState: {
          feedbacks: communication?.feedbacks ?? [],
          communication: icbtCommunicationProps ?? null,
        },
        lastActivity: {
          date: lastProgressAt ? format(lastProgressAt, "yyyy-MM-dd") : "--",
        },
        metadata: {
          programId: program.id,
          treatmentId: program.treatment.id,
          icbtRequestId: entry.request?.id ?? null,
          feedbackDueDate: feedbackDate ?? null,
          feedbackDueDateIgnored: Boolean(
            icbtCommunicationProps?.feedback_date_ignored_at,
          ),
          newProgressAfterLastMeeting: highlightStatus === "info",
        },
        status: highlightStatus,
      };

      if (entry.type === "selfExplored") {
        const { startedAt } = entry.progress;
        const date = startedAt ? format(parseISO(startedAt), "yyyy-MM-dd") : "";

        const unlockItem = getSequentialUnlockItem(
          icbtSequentialUnlock,
          entry.progress.programId,
          program.treatment.id,
        );

        return {
          ...sharedCellsAndData,
          startedAt: date,
          metadata: {
            ...sharedCellsAndData.metadata,
            startedAt: startedAt ?? null,
          },
          status: highlightStatus,
          isLocked: unlockItem?.isLocked ?? false,
          isFeedbackLocked:
            feedbackLockEnabled &&
            (unlockItem?.feedbackLocked?.locked ?? false),
          feedbackLockReason: unlockItem?.feedbackLocked?.reason,
          nextScheduledExecution: unlockItem?.nextScheduledExecution ?? null,
        };
      }

      const { requestedAt, psychologist } = entry.request;
      const assignedAt = parseISO(requestedAt);
      const assignedBy = `${psychologist.firstName} ${psychologist.lastName}`;

      const unlockItem = getSequentialUnlockItem(
        icbtSequentialUnlock,
        entry.request.programId,
        entry.request.treatmentId,
        entry.request.id,
      );

      return {
        ...sharedCellsAndData,
        id: entry.request.id,
        assigned: {
          assignedAt,
          assignedBy,
        },
        status: highlightStatus,
        isLocked: unlockItem?.isLocked ?? false,
        isFeedbackLocked:
          feedbackLockEnabled && (unlockItem?.feedbackLocked?.locked ?? false),
        feedbackLockReason: unlockItem?.feedbackLocked?.reason,
        nextScheduledExecution: unlockItem?.nextScheduledExecution ?? null,
        metadata: {
          ...sharedCellsAndData.metadata,
          requestId: entry.request.id,
          assignedAt,
          requestOrder: entry.request.requestOrder,
          hasProgress: Boolean(lastProgressAt),
          isLatestRequest: entry.request.isLatestRequest,
          programId: entry.request.programId,
          feedbackDate: entry.request.feedbackDate ?? null,
          icbtFeedbackDateIgnored: Boolean(
            icbtCommunicationProps?.feedback_date_ignored_at,
          ),
          hasNoFeedback,
          patientSeenFeedbackAt,
          canExtendFeedbackDate: unlockItem?.canExtendFeedbackDate ?? false,
        },
      };
    }),
    R.values,
    R.partition(
      (tableRow): tableRow is Extract<typeof tableRow, { assigned: unknown }> =>
        "assigned" in tableRow,
    ),
    ([assigned, selfExplored]) => {
      const getProgressCount = R.countBy(
        (item: { metadata: { newProgressAfterLastMeeting: boolean } }) =>
          item.metadata.newProgressAfterLastMeeting,
      );

      return {
        assignedTableRows: R.sortBy(
          assigned,
          ({ metadata: { requestOrder, assignedAt } }) => {
            return sortableIcbtsEnabled ? requestOrder : assignedAt;
          },
          ({ metadata: { requestId } }) => requestId,
        ),
        assignedNewProgressCount: getProgressCount(assigned),
        selfExploredTableRows: R.sortBy(
          selfExplored,
          ({ metadata: { startedAt } }) => startedAt ?? "",
        ),
        selfExploredNewProgressCount: getProgressCount(selfExplored),
      };
    },
  );
};

export const rowHasActivity = <T extends AssignedTableRow>(row: T | Row<T>) => {
  if (!("metadata" in row)) return false;
  if (row.metadata?.hasProgress) {
    return true;
  }
  if (!R.isString(row.feedbackDate) && row.feedbackDate.feedbackDate) {
    return true;
  }
  if (!R.isString(row.title) && row.title.status?.percentage === 100) {
    return true;
  }
  return false;
};

export const getDraggableRowIds = (rows: AssignedTableRow[]): Set<string> =>
  new Set(
    R.pipe(
      rows,
      R.reverse(),
      R.takeWhile((row) => !rowHasActivity(row)),
      R.map((row) => row.id),
    ),
  );
